import { useState, useMemo, useCallback } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import * as utils from "../utils/utils";
import Button from "../components/common/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";
import { Input } from "../components/common/Input";
import { Checkbox } from "../components/common/Checkbox";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { formatClockTimeRange } from "../lib/ScheduleUtils";
import { Share2, Users, Trash2, Clock, ArrowLeft, Search } from "lucide-react";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  getLocalizedAppointmentType,
  getLocalizedDayName,
} from "../i18n/scheduleLabels";

const PROFILE_TEAM_TAB_URL = "/profile?tab=team";

function staffMatchesSearch(profile, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    utils.getStaffDisplayName(profile),
    utils.getStaffDisplaySubtitle(profile),
    profile.email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function StaffSelectOption({ profile }) {
  const subtitle = utils.getStaffDisplaySubtitle(profile);
  return (
    <>
      <span className="font-medium">{utils.getStaffDisplayName(profile)}</span>
      {subtitle ? (
        <span className="block text-xs text-gray-500">{subtitle}</span>
      ) : null}
    </>
  );
}

export default function DoctorShareSchedule() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const {
    schedules,
    secretaries,
    doctorSecretaries,
    sharedSchedules,
    shareSchedule,
    getSchedulesByDoctor,
    removeSharedSchedule,
    loadData,
  } = useData();

  usePeriodicLoadData(loadData, undefined, !!user?.id && user?.role === "doctor");

  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [selectedSecretary, setSelectedSecretary] = useState("");
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [staffSelectOpen, setStaffSelectOpen] = useState(false);

  const resolveStaffName = useCallback(
    (staffId) => {
      const profile = secretaries.find((s) => s.id === staffId);
      return utils.getStaffDisplayName(profile);
    },
    [secretaries]
  );

  const { teamSecretaries, institutions } = useMemo(() => {
    if (!user?.id) {
      return { teamSecretaries: [], institutions: [] };
    }

    const teamIds = new Set(
      doctorSecretaries
        .filter((relation) => relation.doctor_id === user.id)
        .map((relation) => relation.secretary_id)
    );

    const team = secretaries
      .filter((profile) => profile.role === "secretary" && teamIds.has(profile.id))
      .sort((a, b) =>
        utils.getStaffDisplayName(a).localeCompare(utils.getStaffDisplayName(b), "es")
      );

    const inst = secretaries
      .filter((profile) => profile.role === "institution")
      .sort((a, b) =>
        utils.getStaffDisplayName(a).localeCompare(utils.getStaffDisplayName(b), "es")
      );

    return { teamSecretaries: team, institutions: inst };
  }, [doctorSecretaries, secretaries, user?.id]);

  const filteredTeamSecretaries = useMemo(
    () => teamSecretaries.filter((profile) => staffMatchesSearch(profile, staffSearchQuery)),
    [teamSecretaries, staffSearchQuery]
  );

  const filteredInstitutions = useMemo(
    () => institutions.filter((profile) => staffMatchesSearch(profile, staffSearchQuery)),
    [institutions, staffSearchQuery]
  );

  const selectedStaffProfile = useMemo(
    () => secretaries.find((profile) => profile.id === selectedSecretary),
    [secretaries, selectedSecretary]
  );

  const sharedBySecretary = useMemo(() => {
    const withSchedule = sharedSchedules
      .map((shared) => {
        const schedule = schedules.find((s) => s.id === shared.schedule_id);
        return schedule ? { shared, schedule } : null;
      })
      .filter(Boolean);

    const bySecretary = {};
    withSchedule.forEach(({ shared, schedule }) => {
      const id = shared.secretary_id;
      if (!bySecretary[id]) bySecretary[id] = [];
      bySecretary[id].push({ shared, schedule });
    });

    return Object.entries(bySecretary)
      .map(([secretaryId, items]) => ({
        secretaryId,
        secretaryName: resolveStaffName(secretaryId),
        items: items.sort((a, b) => {
          const dayA = Number(a.schedule.day_of_week);
          const dayB = Number(b.schedule.day_of_week);
          if (dayA !== dayB) return dayA - dayB;
          return (a.schedule.start_time || "").localeCompare(b.schedule.start_time || "");
        }),
      }))
      .sort((a, b) => a.secretaryName.localeCompare(b.secretaryName, "es"));
  }, [sharedSchedules, schedules, resolveStaffName]);

  if (!user || user.role !== "doctor") return null;

  const doctorSchedules = getSchedulesByDoctor(user.id).filter(
    (s) => (s.schedule_type || "habitual") !== "blocked"
  );

  const groupedSchedules = doctorSchedules.reduce((acc, schedule) => {
    const key = `${schedule.day_of_week}-${schedule.appointment_type}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(schedule);
    return acc;
  }, {});

  const getSharedScheduleIds = () => {
    return sharedSchedules.map((shared) => shared.schedule_id);
  };

  const sharedScheduleIds = getSharedScheduleIds();

  const handleScheduleToggle = (scheduleId) => {
    setSelectedSchedules((prev) =>
      prev.includes(scheduleId)
        ? prev.filter((id) => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const handleShareSchedules = () => {
    if (selectedSchedules.length === 0) {
      customToast(t("app.share.toastSelectSchedule", "Selecciona al menos un horario para compartir"), "warning");
      return;
    }

    if (!selectedSecretary) {
      customToast(t("app.share.toastSelectSecretary", "Selecciona una secretaria"), "warning");
      return;
    }

    selectedSchedules.forEach((scheduleId) => {
      shareSchedule(scheduleId, selectedSecretary, user.id);
    });

    setSelectedSchedules([]);
    setSelectedSecretary("");
    customToast(t("app.share.toastShared", "Horarios compartidos exitosamente"), "success");
    setStaffSearchQuery("");
  };

  const handleRemoveSharedSchedule = (scheduleId) => {
    if (
      confirm(t("app.share.confirmRemove", "¿Estás seguro de que quieres dejar de compartir este horario?"))
    ) {
      removeSharedSchedule(scheduleId);
      customToast(t("app.share.toastRemoved", "Horario eliminado de compartidos exitosamente"), "success");
    }
  };

  const hasStaffOptions =
    teamSecretaries.length > 0 || institutions.length > 0;
  const showTeamSecretariesGroup =
    teamSecretaries.length === 0 || filteredTeamSecretaries.length > 0;
  const hasFilteredStaffOptions =
    teamSecretaries.length === 0 ||
    filteredTeamSecretaries.length > 0 ||
    filteredInstitutions.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => utils.goBack()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.backHome", "Volver al Inicio")}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Panel para compartir nuevos horarios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  {t("app.share.title", "Compartir Horarios")}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Seleccionar secretaria */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t("app.share.selectStaff", "Seleccionar Secretaria o Institución")}
                </label>
                <Select
                  value={selectedSecretary}
                  onValueChange={setSelectedSecretary}
                  open={staffSelectOpen}
                  onOpenChange={(open) => {
                    setStaffSelectOpen(open);
                    if (!open) setStaffSearchQuery("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "app.share.selectStaffPlaceholder",
                        "Elige una secretaria o institución..."
                      )}
                    >
                      {selectedStaffProfile
                        ? utils.getStaffDisplayName(selectedStaffProfile)
                        : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-80" position="popper">
                    <div
                      className="sticky top-0 z-10 bg-white p-2 border-b border-gray-100"
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          value={staffSearchQuery}
                          onChange={(event) => setStaffSearchQuery(event.target.value)}
                          onKeyDown={(event) => event.stopPropagation()}
                          placeholder={t("app.share.searchStaff", "Buscar por nombre o email...")}
                          className="h-9 pl-8 text-sm"
                        />
                      </div>
                    </div>

                    {!hasFilteredStaffOptions ? (
                      <div className="px-3 py-4 text-sm text-gray-500 text-center">
                        {t("common.noResults", "No se encontraron resultados")}
                      </div>
                    ) : (
                      <>
                        {showTeamSecretariesGroup && (
                          <SelectGroup>
                            <SelectLabel>{t("app.share.teamSecretaries", "Secretarias del equipo")}</SelectLabel>
                            {filteredTeamSecretaries.length > 0 ? (
                              filteredTeamSecretaries.map((profile) => (
                                <SelectItem key={profile.id} value={profile.id}>
                                  <StaffSelectOption profile={profile} />
                                </SelectItem>
                              ))
                            ) : (
                              <div
                                className="py-1.5 pl-8 pr-2 text-sm"
                                onPointerDown={(event) => event.stopPropagation()}
                              >
                                <a
                                  href={PROFILE_TEAM_TAB_URL}
                                  className="text-blue-600 hover:underline"
                                >
                                  {t("app.share.addTeamSecretaries", "Agregar secretarias al equipo")}
                                </a>
                              </div>
                            )}
                          </SelectGroup>
                        )}

                        {filteredInstitutions.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>{t("app.share.institutions", "Instituciones")}</SelectLabel>
                            {filteredInstitutions.map((profile) => (
                              <SelectItem key={profile.id} value={profile.id}>
                                <StaffSelectOption profile={profile} />
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Lista de horarios disponibles para compartir */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t("app.share.availableSchedules", "Horarios Disponibles")}
                </label>
                {Object.keys(groupedSchedules).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    {t("app.share.noSchedules", "No tienes horarios configurados.")}
                    <br />
                    <a
                      href="/doctor/schedule"
                      className="text-blue-600 hover:underline"
                    >
                      {t("app.share.configureSchedules", "Configura tus horarios primero")}
                    </a>
                  </p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {Object.entries(groupedSchedules).map(
                      ([key, scheduleGroup]) => {
                        const [dayOfWeek, type] = key.split("-");
                        const dayName = getLocalizedDayName(dayOfWeek);
                        const typeName = getLocalizedAppointmentType(type);

                        return (
                          <div key={key} className="border rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-2 flex-wrap">
                                  <span>{dayName}</span>
                                  <span className="text-gray-400">-</span>
                                  <Badge
                                    variant={
                                      type === "private"
                                        ? "appointmentPrivate"
                                        : "appointmentInsurance"
                                    }
                                    className="text-xs"
                                  >
                                    {typeName}
                                  </Badge>
                                </h4>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {scheduleGroup.map((schedule) => (
                                <div
                                  key={schedule.id}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`schedule-${schedule.id}`}
                                    checked={selectedSchedules.includes(
                                      schedule.id
                                    )}
                                    onCheckedChange={() =>
                                      handleScheduleToggle(schedule.id)
                                    }
                                    disabled={sharedScheduleIds.includes(
                                      schedule.id
                                    )}
                                  />
                                  <label
                                    htmlFor={`schedule-${schedule.id}`}
                                    className={`text-sm flex-1 ${
                                      sharedScheduleIds.includes(schedule.id)
                                        ? "text-gray-400 line-through"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-3 h-3" />
                                      {formatClockTimeRange(
                                        schedule.start_time,
                                        schedule.end_time
                                      )}
                                      <span className="text-gray-500">
                                        ({t("app.share.slotDuration", "turnos de {{minutes}} min", {
                                          minutes: schedule.slot_duration,
                                        })})
                                      </span>
                                    </div>
                                  </label>
                                  {sharedScheduleIds.includes(schedule.id) && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {t("app.share.alreadyShared", "Ya compartido")}
                                    </Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={handleShareSchedules}
                disabled={
                  selectedSchedules.length === 0 ||
                  !selectedSecretary ||
                  !hasStaffOptions
                }
                className="w-full"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t("app.share.shareSelected", "Compartir Horarios Seleccionados")}
              </Button>
            </CardContent>
          </Card>

          {/* Panel de horarios ya compartidos: agrupado por secretaria, ordenado por día y hora */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t("app.share.sharedWith", "Horarios Compartidos")}
              </CardTitle>
              <p className="text-sm text-gray-500 font-normal mt-1">
                {t(
                  "app.share.groupedHint",
                  "Agrupado por persona; dentro de cada uno, por día y horario."
                )}
              </p>
            </CardHeader>
            <CardContent>
              {sharedBySecretary.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t("app.share.noSharedYet", "No has compartido horarios aún")}
                </p>
              ) : (
                <div className="space-y-6">
                  {sharedBySecretary.map(({ secretaryId, secretaryName, items }) => (
                    <div key={secretaryId} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 font-medium text-gray-800 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {secretaryName}
                        <span className="text-sm font-normal text-gray-600">
                          ({items.length} horario{items.length !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <ul className="divide-y divide-gray-100">
                        {items.map(({ shared, schedule }) => {
                          const dayName = getLocalizedDayName(schedule.day_of_week);
                          const typeName = getLocalizedAppointmentType(
                            schedule.appointment_type
                          );
                          return (
                            <li
                              key={shared.id}
                              className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-gray-900">
                                  {dayName}
                                </span>
                                <span className="text-gray-500 mx-2">·</span>
                                <Badge
                                  variant={
                                    schedule.appointment_type === "private"
                                      ? "appointmentPrivate"
                                      : "appointmentInsurance"
                                  }
                                  className="text-xs"
                                >
                                  {typeName}
                                </Badge>
                                <span className="text-gray-500 ml-2 text-sm">
                                  {formatClockTimeRange(
                                    schedule.start_time,
                                    schedule.end_time
                                  )}
                                  <span className="text-gray-400 ml-1">
                                    ({schedule.slot_duration} min)
                                  </span>
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0 text-gray-500 hover:text-red-600"
                                onClick={() =>
                                  handleRemoveSharedSchedule(schedule.id)
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Información adicional */}
        <Card className="mt-6 mb-20">
          <CardHeader>
            <CardTitle>{t("app.share.infoTitle", "Información Importante")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <h4 className="font-medium">
                  {t("app.share.infoSecretariesTitle", "¿Qué pueden hacer las secretarias?")}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>{t("app.share.infoSecretaries1", "Ver solo los horarios que les has compartido")}</li>
                  <li>{t("app.share.infoSecretaries2", "Asignar turnos en esos slots disponibles")}</li>
                  <li>
                    {t(
                      "app.share.infoSecretaries3",
                      "No pueden ver turnos particulares o de obra social existentes"
                    )}
                  </li>
                  <li>{t("app.share.infoSecretaries4", "No pueden superponer turnos ya programados")}</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("app.share.infoAccessTitle", "Control de acceso")}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>{t("app.share.infoAccess1", "Puedes revocar el acceso en cualquier momento")}</li>
                  <li>{t("app.share.infoAccess2", "Los turnos ya asignados se mantienen")}</li>
                  <li>{t("app.share.infoAccess3", "Las secretarias solo ven información básica")}</li>
                  <li>{t("app.share.infoAccess4", "Tú mantienes el control total de tu agenda")}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
